import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaCamera, FaPlus } from "react-icons/fa";
import Swal from "sweetalert2";
import { useAuth } from "../Login/AuthContext";
import { useConfiguracionSitio } from "../Configuracion/ContextoConfiguracionSitio";

const DashboardPrincipal = () => {
  const { token } = useAuth();
  const { logoUrl, dashboardImagenUrl, imagenPredeterminada, personalizacion, actualizarImagen } = useConfiguracionSitio();
  const [guardando, setGuardando] = useState("");
  const logoInputRef = useRef(null);
  const dashboardInputRef = useRef(null);

  const seleccionarImagen = async (tipo, event) => {
    const imagen = event.target.files?.[0];
    event.target.value = "";
    if (!imagen) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(imagen.type)) {
      Swal.fire("Formato no permitido", "Usa una imagen JPG, PNG o WEBP.", "warning");
      return;
    }
    if (imagen.size > 10 * 1024 * 1024) {
      Swal.fire("Imagen demasiado grande", "La imagen no puede superar los 10 MB.", "warning");
      return;
    }
    try {
      setGuardando(tipo);
      await actualizarImagen(tipo, imagen, token);
      Swal.fire({ icon: "success", title: "Imagen actualizada", timer: 1400, showConfirmButton: false });
    } catch (error) {
      Swal.fire("No se pudo actualizar", error.response?.data?.message || "Intenta nuevamente.", "error");
    } finally {
      setGuardando("");
    }
  };

  return (
    <section className="min-h-screen bg-stone-50 px-4 py-8 md:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg bg-white p-5 shadow-xl md:grid-cols-[1.1fr_0.9fr] md:p-8">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
                Panel de administracion
              </span>
              <Link
                to="/admin/productos/registro"
                className="inline-flex items-center gap-2 rounded-full bg-stone-950 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-stone-300 transition duration-300 hover:-translate-y-0.5 hover:bg-amber-700 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              >
                <FaPlus aria-hidden="true" />
                Agregar nuevo producto
              </Link>
            </div>
            <h2 className="mt-3 text-3xl font-extrabold text-stone-900 md:text-5xl">
              {personalizacion.dashboard_titulo}
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-stone-600 md:text-lg">
              {personalizacion.dashboard_descripcion}
            </p>

            <div className="mt-6 flex flex-wrap gap-3 text-sm font-medium text-stone-700">
              <span className="rounded-full bg-amber-100 px-4 py-2">Catalogo</span>
              <span className="rounded-full bg-rose-100 px-4 py-2">Stock</span>
              <span className="rounded-full bg-emerald-100 px-4 py-2">Ventas</span>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-lg bg-stone-200">
            <img
              src={dashboardImagenUrl}
              alt="Tienda de arte"
              className="h-72 w-full object-cover md:h-96"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = imagenPredeterminada;
              }}
            />
            <button type="button" onClick={() => dashboardInputRef.current?.click()} disabled={Boolean(guardando)} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-black/85 px-4 py-2.5 text-sm font-bold text-white shadow-lg backdrop-blur transition hover:bg-amber-700 disabled:opacity-60">
              <FaCamera /> {guardando === "dashboard" ? "Subiendo..." : "Cambiar imagen"}
            </button>
            <input ref={dashboardInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => seleccionarImagen("dashboard", event)} />
          </div>
        </div>

        <div className="flex flex-col gap-5 rounded-lg border border-stone-200 bg-white p-5 shadow-md sm:flex-row sm:items-center sm:justify-between md:p-6">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="Logo actual de Casa Arte" className="h-20 w-20 rounded-full border-2 border-amber-200 object-cover shadow-sm" onError={({ currentTarget }) => { currentTarget.onerror = null; currentTarget.src = imagenPredeterminada; }} />
            <div>
              <h3 className="text-lg font-bold text-stone-900">Logo de la tienda</h3>
              <p className="mt-1 text-sm text-stone-600">Aparece arriba a la izquierda y en el pie de pagina.</p>
            </div>
          </div>
          <button type="button" onClick={() => logoInputRef.current?.click()} disabled={Boolean(guardando)} className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-3 text-sm font-bold text-stone-900 transition hover:border-amber-500 hover:bg-amber-50 disabled:opacity-60">
            <FaCamera /> {guardando === "logo" ? "Subiendo..." : "Cambiar logo"}
          </button>
          <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => seleccionarImagen("logo", event)} />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-md">
            <h3 className="mb-3 text-xl font-semibold text-stone-900">
              Gestion de obras
            </h3>
            <p className="text-sm leading-relaxed text-stone-600">
              Agrega, edita y organiza pinturas, esculturas, laminas,
              artesanias y piezas unicas con sus imagenes y detalles.
            </p>
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-md">
            <h3 className="mb-3 text-xl font-semibold text-stone-900">
              Control de inventario
            </h3>
            <p className="text-sm leading-relaxed text-stone-600">
              Revisa stock, precios y disponibilidad para que cada producto
              publicado refleje el estado real de la tienda.
            </p>
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-md">
            <h3 className="mb-3 text-xl font-semibold text-stone-900">
              Experiencia de compra
            </h3>
            <p className="text-sm leading-relaxed text-stone-600">
              Presenta el catalogo de forma visual para que los clientes puedan
              explorar piezas, ver detalles y comprar con facilidad.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-md md:p-6">
          <p className="text-sm leading-relaxed text-stone-600 md:text-base">
            Usa el menu principal para entrar al listado de productos, cargar
            nuevas piezas y administrar el catalogo de la tienda de arte.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DashboardPrincipal;
